/**
 * DraggableChart Component - Interactive cost curve visualization
 * Requirements: 3.9, 3.10, 6.1, 6.2
 */

import { useMemo, useCallback, useState, useRef } from 'react';
import {
  LineChart,
  Line,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ReferenceLine,
  ResponsiveContainer,
} from 'recharts';

interface CostCurvePoint {
  volume: number;
  costPerCase: number;
}

interface DraggableChartProps {
  fixedCost: number;
  currentVolume: number;
  maxVolume: number;
  scenarioVolume?: number;
  onDrag?: (newVolume: number) => void;
}

function DraggableChart({
  fixedCost,
  currentVolume,
  maxVolume,
  scenarioVolume,
  onDrag,
}: DraggableChartProps) {
  const chartRef = useRef<HTMLDivElement>(null);
  const [isDragging, setIsDragging] = useState(false);

  // Generate cost curve data points - ensure current and scenario volumes are included
  const data = useMemo((): CostCurvePoint[] => {
    const points: CostCurvePoint[] = [];
    const minVolume = Math.max(1000000, currentVolume * 0.5);
    const effectiveMax = Math.max(maxVolume, scenarioVolume || 0, currentVolume) * 1.1;
    const steps = 50;
    const stepSize = (effectiveMax - minVolume) / steps;

    // Generate regular curve points
    for (let i = 0; i <= steps; i++) {
      const volume = minVolume + i * stepSize;
      const costPerCase = volume > 0 ? fixedCost / volume : 0;
      points.push({
        volume: Math.round(volume),
        costPerCase: parseFloat(costPerCase.toFixed(4)),
      });
    }

    // Ensure current volume is included as a data point
    if (!points.some(p => Math.abs(p.volume - currentVolume) < stepSize * 0.1)) {
      const costPerCase = currentVolume > 0 ? fixedCost / currentVolume : 0;
      points.push({
        volume: currentVolume,
        costPerCase: parseFloat(costPerCase.toFixed(4)),
      });
    }

    // Ensure scenario volume is included as a data point
    if (scenarioVolume && scenarioVolume !== currentVolume && 
        !points.some(p => Math.abs(p.volume - scenarioVolume) < stepSize * 0.1)) {
      const costPerCase = scenarioVolume > 0 ? fixedCost / scenarioVolume : 0;
      points.push({
        volume: scenarioVolume,
        costPerCase: parseFloat(costPerCase.toFixed(4)),
      });
    }

    // Sort by volume for proper line rendering
    return points.sort((a, b) => a.volume - b.volume);
  }, [fixedCost, currentVolume, maxVolume, scenarioVolume]);

  // Calculate cost per case for current and scenario volumes
  const currentCostPerCase = currentVolume > 0 ? fixedCost / currentVolume : 0;
  const scenarioCostPerCase = scenarioVolume && scenarioVolume > 0 
    ? fixedCost / scenarioVolume 
    : currentCostPerCase;

  const formatVolume = (value: number): string => {
    if (value >= 1000000) {
      return `${(value / 1000000).toFixed(1)}M`;
    }
    if (value >= 1000) {
      return `${(value / 1000).toFixed(0)}K`;
    }
    return value.toString();
  };

  const formatCost = (value: number): string => {
    return `$${value.toFixed(2)}`;
  };

  // Handle drag interaction
  const handleMouseDown = useCallback(() => {
    if (onDrag) {
      setIsDragging(true);
    }
  }, [onDrag]);

  const handleMouseUp = useCallback(() => {
    setIsDragging(false);
  }, []);

  const handleMouseMove = useCallback(
    (e: React.MouseEvent) => {
      if (!isDragging || !onDrag || !chartRef.current) return;

      const rect = chartRef.current.getBoundingClientRect();
      const chartWidth = rect.width - 60; // Account for margins
      const x = e.clientX - rect.left - 40; // Account for left margin
      const ratio = Math.max(0, Math.min(1, x / chartWidth));
      
      const minVolume = Math.max(1000000, currentVolume * 0.5);
      const newVolume = Math.round(minVolume + ratio * (maxVolume - minVolume));
      onDrag(newVolume);
    },
    [isDragging, onDrag, currentVolume, maxVolume]
  );

  const CustomTooltip = ({ active, payload }: { active?: boolean; payload?: Array<{ payload: CostCurvePoint }> }) => {
    if (active && payload && payload.length > 0) {
      const point = payload[0]?.payload;
      if (!point) return null;
      return (
        <div style={styles.tooltip}>
          <p style={styles.tooltipLine}>Volume: {formatVolume(point.volume)}</p>
          <p style={styles.tooltipLine}>Cost/Case: {formatCost(point.costPerCase)}</p>
        </div>
      );
    }
    return null;
  };

  return (
    <div style={styles.container}>
      <div style={styles.header}>
        <h4 style={styles.title}>Cost Curve</h4>
        <div style={styles.legend}>
          <span style={styles.legendItem}>
            <span style={{ ...styles.legendDot, backgroundColor: '#0066cc' }} />
            Current
          </span>
          {scenarioVolume && scenarioVolume !== currentVolume && (
            <span style={styles.legendItem}>
              <span style={{ ...styles.legendDot, backgroundColor: '#28a745' }} />
              Scenario
            </span>
          )}
        </div>
      </div>

      <div
        ref={chartRef}
        style={styles.chartContainer}
        onMouseDown={handleMouseDown}
        onMouseUp={handleMouseUp}
        onMouseLeave={handleMouseUp}
        onMouseMove={handleMouseMove}
      >
        <ResponsiveContainer width="100%" height={300}>
          <LineChart data={data} margin={{ top: 20, right: 20, left: 10, bottom: 10 }}>
            <CartesianGrid strokeDasharray="3 3" stroke="#eee" />
            <XAxis
              dataKey="volume"
              type="number"
              domain={['dataMin', 'dataMax']}
              tickFormatter={formatVolume}
              tick={{ fontSize: 10 }}
              stroke="#888"
            />
            <YAxis
              tickFormatter={formatCost}
              tick={{ fontSize: 10 }}
              stroke="#888"
              domain={['auto', 'auto']}
            />
            <Tooltip content={<CustomTooltip />} />
            
            {/* Cost curve line */}
            <Line
              type="monotone"
              dataKey="costPerCase"
              stroke="#666"
              strokeWidth={2}
              dot={false}
              activeDot={{ r: 6 }}
            />

            {/* Current position marker */}
            <ReferenceLine
              x={currentVolume}
              stroke="#0066cc"
              strokeWidth={2}
              strokeDasharray="5 5"
              label={{
                value: 'Current',
                position: 'top',
                fill: '#0066cc',
                fontSize: 10,
              }}
            />

            {/* Scenario position marker */}
            {scenarioVolume && scenarioVolume !== currentVolume && (
              <ReferenceLine
                x={scenarioVolume}
                stroke="#28a745"
                strokeWidth={2}
                label={{
                  value: 'Scenario',
                  position: 'top',
                  fill: '#28a745',
                  fontSize: 10,
                }}
              />
            )}
          </LineChart>
        </ResponsiveContainer>
      </div>

      <div style={styles.metrics}>
        <div style={styles.metricItem}>
          <span style={styles.metricLabel}>Current</span>
          <span style={styles.metricValue}>{formatCost(currentCostPerCase)}</span>
          <span style={styles.metricSubtext}>{formatVolume(currentVolume)} cases</span>
        </div>
        {scenarioVolume && scenarioVolume !== currentVolume && (
          <>
            <div style={styles.metricArrow}>→</div>
            <div style={styles.metricItem}>
              <span style={styles.metricLabel}>Scenario</span>
              <span style={{ ...styles.metricValue, color: '#28a745' }}>
                {formatCost(scenarioCostPerCase)}
              </span>
              <span style={styles.metricSubtext}>{formatVolume(scenarioVolume)} cases</span>
            </div>
          </>
        )}
      </div>

      {onDrag && (
        <p style={styles.hint}>
          Drag on the chart to adjust scenario volume
        </p>
      )}
    </div>
  );
}

const styles: Record<string, React.CSSProperties> = {
  container: {
    padding: '0.5rem 0',
  },
  header: {
    display: 'flex',
    justifyContent: 'space-between',
    alignItems: 'center',
    marginBottom: '0.5rem',
  },
  title: {
    margin: 0,
    fontSize: '0.9rem',
    color: '#333',
    fontWeight: 600,
  },
  legend: {
    display: 'flex',
    gap: '1rem',
  },
  legendItem: {
    display: 'flex',
    alignItems: 'center',
    gap: '0.25rem',
    fontSize: '0.75rem',
    color: '#666',
  },
  legendDot: {
    width: '8px',
    height: '8px',
    borderRadius: '50%',
  },
  chartContainer: {
    cursor: 'crosshair',
  },
  tooltip: {
    backgroundColor: '#fff',
    border: '1px solid #ddd',
    borderRadius: '4px',
    padding: '0.5rem',
    boxShadow: '0 2px 4px rgba(0,0,0,0.1)',
  },
  tooltipLine: {
    margin: '0.25rem 0',
    fontSize: '0.8rem',
    color: '#333',
  },
  metrics: {
    display: 'flex',
    justifyContent: 'center',
    alignItems: 'center',
    gap: '1rem',
    marginTop: '0.5rem',
    padding: '0.5rem',
    backgroundColor: '#f8f9fa',
    borderRadius: '4px',
  },
  metricItem: {
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
  },
  metricLabel: {
    fontSize: '0.7rem',
    color: '#888',
    textTransform: 'uppercase',
  },
  metricValue: {
    fontSize: '1rem',
    fontWeight: 600,
    color: '#0066cc',
  },
  metricSubtext: {
    fontSize: '0.7rem',
    color: '#888',
  },
  metricArrow: {
    fontSize: '1.25rem',
    color: '#999',
  },
  hint: {
    textAlign: 'center',
    fontSize: '0.75rem',
    color: '#999',
    fontStyle: 'italic',
    margin: '0.5rem 0 0 0',
  },
};

export default DraggableChart;
